import { toBlob } from 'html-to-image';

// 1x1 transparent gif — fallback when a competitor logo can't be inlined
// because the public S3 bucket doesn't return Access-Control-Allow-Origin.
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 * Find each FitToWidth's innermost scaled wrapper inside the bracket. These
 * are positioned `absolute, top:0, left:0` with `transform: scale(s)` and
 * carry the *natural* full bracket dimensions (e.g. 1500×358) while the
 * parent wrappers above them clip to the on-screen viewport.
 *
 * Capturing one of these inner wrappers directly gives the full unclipped
 * bracket — we just need to override its inline `transform: scale(...)` to
 * `none` during capture so html-to-image renders at 1:1 instead of the
 * shrunk view the user is currently seeing.
 */
function findScaledBracketLayers(root: HTMLElement): HTMLElement[] {
  const candidates = root.querySelectorAll<HTMLElement>('[style*="transform"]');
  return [...candidates].filter((el) => el.style.transform.includes('scale'));
}

export type CaptureBracketOptions = {
  /** Event name rendered as the title above the bracket. */
  eventName?: string;
  /** Optional hashtag printed in the footer (e.g. "#FlyQuestPickem"). */
  hashtag?: string;
};

export async function captureBracketImage(
  element: HTMLElement,
  options: CaptureBracketOptions = {},
): Promise<Blob> {
  // html-to-image walks every stylesheet to inline rules. Cross-origin sheets
  // (Google's GSI, ad pixels, etc.) throw on cssRules access — and the library
  // doesn't bail, it hangs trying to fetch them. Detach them for the duration
  // of the capture, then put them back.
  const externalStylesheets = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  ).filter((link) => {
    try {
      const url = new URL(link.href, document.baseURI);
      return url.origin !== window.location.origin;
    } catch {
      return false;
    }
  });
  const parked = externalStylesheets.map((link) => ({
    link,
    parent: link.parentNode,
    next: link.nextSibling,
  }));
  for (const { link } of parked) link.remove();

  // Pick the first scaled FitToWidth layer if present — capturing it directly
  // gives the full bracket without the parent's overflow:hidden clipping. If
  // there's no scaling (e.g. desktop with enough room), fall back to the
  // outer element.
  const scaledLayers = findScaledBracketLayers(element);
  const target = scaledLayers[0] ?? element;

  try {
    const capturePromise = toBlob(target, {
      backgroundColor: '#0a0a0a',
      pixelRatio: window.devicePixelRatio > 1 ? 2 : 1.5,
      width: target.offsetWidth,
      height: target.offsetHeight,
      skipFonts: true,
      imagePlaceholder: TRANSPARENT_PIXEL,
      style: { transform: 'none' },
    });
    const timeoutMs = 30_000;
    const blob = await Promise.race([
      capturePromise,
      new Promise<null>((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(`Bracket capture timed out after ${timeoutMs}ms`),
            ),
          timeoutMs,
        ),
      ),
    ]);
    if (!blob) throw new Error('Failed to encode bracket image');
    return composeShareImage(blob, options);
  } finally {
    for (const { link, parent, next } of parked) {
      if (parent) parent.insertBefore(link, next);
    }
  }
}

async function composeShareImage(
  bracketBlob: Blob,
  options: CaptureBracketOptions,
): Promise<Blob> {
  const bracketImg = await blobToImage(bracketBlob);

  const HPAD = 96;
  const VPAD_TOP = 120;
  const VPAD_BOTTOM = 120;
  const KICKER_SIZE = 36;
  const TITLE_SIZE = 84;
  const TITLE_GAP_BELOW = 72;
  const FOOTER_GAP_ABOVE = 64;
  const FOOTER_SIZE = 44;

  const hasTitle = !!options.eventName;
  const hasFooter = !!options.hashtag;

  const headerHeight = hasTitle
    ? KICKER_SIZE + 24 + TITLE_SIZE + TITLE_GAP_BELOW
    : 0;
  const footerHeight = hasFooter ? FOOTER_SIZE + FOOTER_GAP_ABOVE : 0;

  const width = bracketImg.width + HPAD * 2;
  const height =
    bracketImg.height + VPAD_TOP + VPAD_BOTTOM + headerHeight + footerHeight;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // Background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, width, height);

  let cursorY = VPAD_TOP;

  if (hasTitle) {
    ctx.fillStyle = '#a3a3a3';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `600 ${KICKER_SIZE}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif`;
    ctx.fillText('MAINSTAGE PICKEM', width / 2, cursorY);
    cursorY += KICKER_SIZE + 24;

    ctx.fillStyle = '#f5f5f5';
    ctx.font = `700 ${TITLE_SIZE}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif`;
    drawWrappedText(
      ctx,
      options.eventName ?? '',
      width / 2,
      cursorY,
      width - HPAD * 2,
      TITLE_SIZE * 1.15,
    );
    cursorY += TITLE_SIZE + TITLE_GAP_BELOW;
  }

  ctx.drawImage(bracketImg, HPAD, cursorY);
  cursorY += bracketImg.height;

  if (hasFooter) {
    cursorY += FOOTER_GAP_ABOVE;
    ctx.fillStyle = '#34d399';
    ctx.font = `600 ${FOOTER_SIZE}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(options.hashtag ?? '', width / 2, cursorY);
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to encode composed share image'));
      },
      'image/png',
      0.95,
    );
  });
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], x, y + i * lineHeight);
  }
  return y + lines.length * lineHeight;
}

export type PresignedResponse = {
  uploadUrl: string;
  imageKey: string;
  publicUrl: string;
  expiresIn: number;
};

export type UploadDeps = {
  apiBaseUrl: string;
  getAuthToken: () => string | null;
};

export async function uploadPickemShareImage(
  eventId: string,
  blob: Blob,
  deps: UploadDeps,
): Promise<{ publicUrl: string; imageKey: string }> {
  const token = deps.getAuthToken();
  if (!token) {
    throw new Error('You must be signed in to share an image');
  }

  const base = deps.apiBaseUrl.replace(/\/$/, '');
  const presignedRes = await fetch(
    `${base}/api/v1/pickem/events/${eventId}/share-image-upload`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ fileType: blob.type || 'image/png' }),
    },
  );
  if (!presignedRes.ok) {
    throw new Error(`Failed to get upload URL (${presignedRes.status})`);
  }
  const presigned = (await presignedRes.json()) as PresignedResponse;

  const putRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': blob.type || 'image/png' },
  });
  if (!putRes.ok) {
    throw new Error(`Failed to upload image (${putRes.status})`);
  }

  return {
    publicUrl: presigned.publicUrl,
    imageKey: presigned.imageKey,
  };
}
